import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

// Build a user object instantly from the JWT user_metadata — no DB query needed
const buildUserFromJWT = (authUser) => {
  if (!authUser) return null;
  const meta = authUser.user_metadata || {};
  return {
    id: authUser.id,
    email: authUser.email,
    phone: authUser.phone || '',
    firstName: meta.first_name || '',
    lastName: meta.last_name || '',
    role: meta.role || 'PATIENT',
    isActive: true,
    coachId: null,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ref always holds the latest user — used as fallback when DB is unavailable,
  // so a token refresh never silently downgrades the role back to PATIENT.
  const userRef = useRef(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Fetch role and profile data from DB — returns enriched user object
  // Has an 8-second timeout so login never hangs indefinitely if DB is slow
  const buildUserFromProfile = async (authUser) => {
    if (!authUser) return null;
    try {
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 8000)
      );
      const { data: profile } = await Promise.race([profilePromise, timeoutPromise]);
      if (profile) {
        return {
          id: authUser.id,
          email: authUser.email,
          phone: authUser.phone || profile.phone || '',
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          role: profile.role || 'PATIENT',
          isActive: profile.is_active ?? true,
          coachId: profile.coach_id || null,
        };
      }
    } catch (_) {
      // DB unavailable or timed out
    }
    // Prefer the in-memory user (has the correct DB role) over stale JWT metadata.
    // This prevents a token refresh from silently resetting role to PATIENT.
    if (userRef.current?.id === authUser.id) return userRef.current;
    return buildUserFromJWT(authUser);
  };

  useEffect(() => {
    // Check active session on mount — await profile so role is correct before render
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const enrichedUser = await buildUserFromProfile(session.user);
        setUser(enrichedUser);
      }
      setLoading(false);
    });

    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          const enrichedUser = await buildUserFromProfile(session.user);
          setUser(enrichedUser);
        } else {
          setUser(null);
        }
        if (event === 'INITIAL_SESSION') setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Email + password login — awaits profile so role is correct on return
  const login = async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    const enrichedUser = await buildUserFromProfile(data.user);
    setUser(enrichedUser);
    return enrichedUser;
  };

  // Email + password register
  const register = async ({ email, password, firstName, lastName, phone, role = 'PATIENT' }) => {
    const siteUrl = process.env.NODE_ENV === 'production'
      ? 'https://www.fitnession.fit'
      : window.location.origin;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { first_name: firstName, last_name: lastName, role },
        emailRedirectTo: `${siteUrl}/dashboard`,
      },
    });
    if (error) throw error;

    if (phone && data.user) {
      await supabase.from('profiles').update({ phone }).eq('id', data.user.id);
    }

    const enrichedUser = await buildUserFromProfile(data.user);
    setUser(enrichedUser);
    return enrichedUser;
  };

  // Phone OTP — Step 1
  const sendOtp = async (phone) => {
    const { error } = await supabase.auth.signInWithOtp({ phone });
    if (error) throw error;
  };

  // Phone OTP — Step 2: verify and login
  const verifyOtp = async (phone, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms',
    });
    if (error) throw error;
    const enrichedUser = await buildUserFromProfile(data.user);
    setUser(enrichedUser);
    return enrichedUser;
  };

  // Forgot password — sends reset email via Supabase
  const forgotPassword = async (email) => {
    const siteUrl = process.env.NODE_ENV === 'production'
      ? 'https://www.fitnession.fit'
      : window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/reset-password`,
    });
    if (error) throw error;
  };

  // Reset password (called on /reset-password page after clicking email link)
  const resetPassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  // Logout
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  // Update local user state (e.g., after profile edit)
  const updateUser = (updatedFields) => {
    setUser((prev) => ({ ...prev, ...updatedFields }));
  };

  const value = {
    user,
    login,
    register,
    logout,
    sendOtp,
    verifyOtp,
    forgotPassword,
    resetPassword,
    updateUser,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
